import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

interface PalantirStatus {
  provider: string;
  apiUrl: string;
  model: string;
  available: boolean;
  currentProvider: string;
}

interface LLMResponse {
  response: string;
  provider: string;
  model: string;
  usage: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  elapsedSeconds: number;
}

const PalantirLLMTest: React.FC = () => {
  const { apiRequest } = useAuth();
  const [status, setStatus] = useState<PalantirStatus | null>(null);
  const [prompt, setPrompt] = useState('What is 2 + 2? Explain your reasoning briefly.');
  const [response, setResponse] = useState<LLMResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setStatusLoading(true);
      const data = await apiRequest<PalantirStatus & { success: boolean; error?: string; currentProvider: string }>({
        method: 'GET',
        url: '/ui/admin/palantir/status'
      });
      if (data.success) {
        setStatus(data);
      } else {
        setError(data.error || 'Failed to fetch status');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setStatusLoading(false);
    }
  };

  const testLLM = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const data = await apiRequest<LLMResponse & { success: boolean; error?: string }>({
        method: 'POST',
        url: '/ui/admin/palantir/test',
        data: { prompt }
      });

      if (data.success) {
        setResponse(data);
      } else {
        setError(data.error || 'Test failed');
      }
    } catch (err) {
      setError('Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div
        style={styles.header}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h3 style={styles.title}>
          {isCollapsed ? '▶' : '▼'} Palantir LLM Test
        </h3>
        {status && (
          <span style={{
            ...styles.badge,
            backgroundColor: status.available ? '#10b981' : '#ef4444'
          }}>
            {status.provider.toUpperCase()} - {status.available ? 'Available' : 'Unavailable'}
          </span>
        )}
      </div>

      {!isCollapsed && (
        <div style={styles.content}>
          {statusLoading ? (
            <p>Loading status...</p>
          ) : status ? (
            <>
              <div style={styles.statusGrid}>
                <div style={styles.statusItem}>
                  <span style={styles.label}>Provider:</span>
                  <span style={styles.value}>{status.provider}</span>
                </div>
                <div style={styles.statusItem}>
                  <span style={styles.label}>Model:</span>
                  <span style={styles.value}>{status.model}</span>
                </div>
                <div style={styles.statusItem}>
                  <span style={styles.label}>API URL:</span>
                  <span style={styles.value}>{status.apiUrl}</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Prompt:</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  style={styles.textarea}
                  rows={3}
                  placeholder="Enter your prompt here..."
                />
              </div>

              <button
                onClick={testLLM}
                disabled={loading || !prompt.trim()}
                style={{
                  ...styles.button,
                  opacity: loading || !prompt.trim() ? 0.6 : 1
                }}
              >
                {loading ? 'Testing...' : 'Send Test'}
              </button>

              {error && (
                <div style={styles.error}>
                  {error}
                </div>
              )}

              {response && (
                <div style={styles.responseContainer}>
                  <div style={styles.responseMeta}>
                    <span>Provider: <strong>{response.provider}</strong></span>
                    <span>Model: <strong>{response.model}</strong></span>
                    <span>Time: <strong>{response.elapsedSeconds}s</strong></span>
                    {response.usage.total_tokens && (
                      <span>Tokens: <strong>{response.usage.total_tokens}</strong></span>
                    )}
                  </div>
                  <div style={styles.responseText}>
                    {response.response}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p style={styles.error}>{error || 'Failed to load status'}</p>
          )}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '20px',
    backgroundColor: '#fff',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    backgroundColor: '#f9fafb',
    borderRadius: '8px 8px 0 0',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'white',
  },
  content: {
    padding: '16px',
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
  },
  statusItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '4px',
  },
  value: {
    fontSize: '14px',
    color: '#111827',
    fontFamily: 'monospace',
  },
  formGroup: {
    marginBottom: '16px',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  error: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#dc2626',
    fontSize: '14px',
  },
  responseContainer: {
    marginTop: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  responseMeta: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    padding: '10px 12px',
    backgroundColor: '#f3f4f6',
    fontSize: '12px',
    color: '#6b7280',
    borderBottom: '1px solid #e5e7eb',
  },
  responseText: {
    padding: '16px',
    fontSize: '14px',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    backgroundColor: '#fff',
  },
};

export default PalantirLLMTest;
